import React, { useState } from 'react';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import './Roadmap.css';

/* ── Inline icons ───────────────────────────────────────────── */
function IconMonitor() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
    </svg>
  );
}
function IconChat() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}
function IconCode() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
    </svg>
  );
}
var ICON_MAP = { monitor: IconMonitor, chat: IconChat, code: IconCode };

/* ── 2026 release timeline visualization ────────────────────── */
var MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function RoadmapViz() {
  // Shipped: Jan(0)–Apr(3). TODAY: May(4). Upcoming cluster: Dec(11).
  return (
    <div className="rm-tl-section">
      <div className="rm-tl-inner">
        <span className="rm-tl-kicker">2026 RELEASE TIMELINE</span>
        <div className="rm-tl-grid" role="img" aria-label="2026 product release timeline">
          {MONTHS_SHORT.map(function(m, i) {
            var isShipped  = i <= 3;
            var isUpcoming = i === 11;
            var isNow      = i === 4;
            return (
              <div
                key={m}
                className={'rm-tl-col'
                  + (isShipped  ? ' rm-tl-col--shipped'  : '')
                  + (isUpcoming ? ' rm-tl-col--upcoming' : '')
                  + (isNow      ? ' rm-tl-col--now'      : '')}
              >
                <div className="rm-tl-above">
                  {isShipped  && <span className="rm-tl-rel-name">{PAST_RELEASES[i].title}</span>}
                  {isUpcoming && <span className="rm-tl-rel-name">3 planned</span>}
                  {isNow      && <span className="rm-tl-now-badge">NOW</span>}
                </div>
                <div className="rm-tl-track-row">
                  {(isShipped || isUpcoming || isNow) && (
                    <div className={'rm-tl-node'
                      + (isShipped  ? ' rm-tl-node--shipped'  : '')
                      + (isUpcoming ? ' rm-tl-node--upcoming' : '')
                      + (isNow      ? ' rm-tl-node--now'      : '')} />
                  )}
                </div>
                <div className={'rm-tl-mon' + (isNow ? ' rm-tl-mon--now' : '')}>{m}</div>
              </div>
            );
          })}
        </div>
        <div className="rm-tl-legend">
          <span className="rm-tl-leg rm-tl-leg--shipped">Shipped</span>
          <span className="rm-tl-leg rm-tl-leg--upcoming">Planned — Dec 2026</span>
        </div>
      </div>
    </div>
  );
}


/* ── Static release data ────────────────────────────────────── */
var PAST_RELEASES = [
  {
    id: 'jan2026', product: 'bot', period: 'January 2026', title: 'B-esper Bot 1.0', releaseDate: '2026-01-31',
    features: ['User Management', 'Tenant Management', 'All B-esper Platform features'],
  },
  {
    id: 'feb2026', product: 'bot', period: 'February 2026', title: 'Analytics & Mobile', releaseDate: '2026-02-28',
    features: [
      'Mobile-friendly Bot Management UI',
      'Advanced Conversational Analytics',
      'Report Generation (PDF reports and presentations)',
      'Bug Report UI in B-esper Products and Platform',
    ],
  },
  {
    id: 'mar2026', product: 'bot', period: 'March 2026', title: 'Integrations Alpha', releaseDate: '2026-03-31',
    features: [
      'B-esper Bot Integrations Alpha (tools support)',
      'Communication Feature leaves Alpha (GA release)',
    ],
  },
  {
    id: 'apr2026', product: 'platform', period: 'April 2026', title: 'Developer Tools', releaseDate: '2026-04-30',
    features: [
      'B-esper Command Line Tools',
      'Advanced Documentation for all B-esper APIs',
      'Chat with B-esper functionality',
    ],
  },
];

var UPCOMING_RELEASES = [
  {
    id: 'dec2026-desktop',
    product: 'platform',
    period: 'December 2026',
    title: 'B-esper Desktop',
    releaseDate: '2026-12-31',
    icon: 'monitor',
    desc: 'A native desktop application that puts your B-esper networks at your fingertips — browse clusters, manage services, and deploy straight from your machine.',
    features: [
      'Native app for Windows, macOS, and Linux',
      'Visual network and cluster browser',
      'Deploy and manage services from your desktop',
      'Local K8s integration for development workflows',
    ],
  },
  {
    id: 'dec2026-livechat',
    product: 'bot',
    period: 'December 2026',
    title: 'Live Chat',
    releaseDate: '2026-12-31',
    icon: 'chat',
    desc: 'Human takeover for B-esper Bot: join any conversation live, guide your customers in real time, and teach the bot to respond more consistently with your voice.',
    features: [
      'Live agent takeover of active bot conversations',
      'Real-time chat handoff with zero interruption',
      'Train Besper Bot from operator corrections and guidance',
      'Conversation history and quality review tools',
    ],
  },
  {
    id: 'dec2026-oss',
    product: 'platform',
    period: 'December 2026',
    title: 'Open Source',
    releaseDate: '2026-12-31',
    icon: 'code',
    desc: 'The B-esper platform core is published on GitHub under the MIT licence — fully open, fully auditable, and ready for community contributions.',
    features: [
      'Full platform source published on GitHub (MIT)',
      'Community contributions open',
      'Complete self-host documentation and guides',
      'Verified build pipeline for independent audits',
    ],
  },
];

var Roadmap = function() {
  var darkCtx = useDarkMode();
  var isDarkMode = darkCtx.isDarkMode;
  var transCtx = useTranslation();
  var language = transCtx.language;

  var fmtDate = function(dateStr) {
    return new Date(dateStr).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  var pastExpandedState = useState(false);
  var pastExpanded = pastExpandedState[0];
  var setPastExpanded = pastExpandedState[1];

  var expandedState = useState({});
  var expandedItems = expandedState[0];
  var setExpandedItems = expandedState[1];

  var toggleItem = function(id) {
    setExpandedItems(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = !prev[id];
      return next;
    });
  };

  var themeClass = isDarkMode ? 'rm-dark' : 'rm-light';

  var filterState = useState('all');
  var activeFilter = filterState[0];
  var setActiveFilter = filterState[1];

  var filteredPast = activeFilter === 'all' ? PAST_RELEASES : PAST_RELEASES.filter(function(r) { return r.product === activeFilter; });
  var filteredUpcoming = activeFilter === 'all' ? UPCOMING_RELEASES : UPCOMING_RELEASES.filter(function(r) { return r.product === activeFilter; });

  var handleFilter = function(f) {
    setActiveFilter(f);
    setPastExpanded(false);
  };

  return (
    <div className={'rm-page ' + themeClass}>

      {/* ── Hero header ── */}
      <div className="rm-header">
        <div className="rm-header-inner">
          <div className="rm-header-row">
            <div className="rm-header-copy">
              <span className="rm-eyebrow">PRODUCT ROADMAP</span>
              <h1 className="rm-title">What we&apos;re building</h1>
              <p className="rm-subtitle">
                Track shipped releases and what&apos;s coming next. Dates are targets—not guarantees.
              </p>
            </div>
            <div className="rm-header-stats">
              <div className="rm-stat">
                <div className="rm-stat-value">{filteredPast.length}</div>
                <div className="rm-stat-label">Shipped</div>
              </div>
              <div className="rm-stat-divider" />
              <div className="rm-stat">
                <div className="rm-stat-value">{filteredUpcoming.length}</div>
                <div className="rm-stat-label">Dec 2026</div>
              </div>
            </div>
          </div>
          <div className="rm-filter-strip">
            <button className={'rm-filter-tab' + (activeFilter === 'all' ? ' rm-filter-tab--active' : '')} onClick={function() { handleFilter('all'); }}>All</button>
            <button className={'rm-filter-tab' + (activeFilter === 'platform' ? ' rm-filter-tab--active' : '')} onClick={function() { handleFilter('platform'); }}>Platform</button>
            <button className={'rm-filter-tab' + (activeFilter === 'bot' ? ' rm-filter-tab--active' : '')} onClick={function() { handleFilter('bot'); }}>B-esper Bot</button>
          </div>
        </div>
      </div>

      {/* ── Network / timeline visualization ── */}
      <RoadmapViz />

      <div className="rm-body">

        {/* ── Past deployments ── */}
        <section className="rm-past-section">
          <button
            className={'rm-past-toggle' + (pastExpanded ? ' rm-past-toggle--open' : '')}
            onClick={function() { setPastExpanded(function(v) { return !v; }); }}
            aria-expanded={pastExpanded}
          >
            <span className="rm-past-check-icon">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="rm-past-toggle-label">
              {filteredPast.length} shipped release{filteredPast.length !== 1 ? 's' : ''}
            </span>
            <span className="rm-past-toggle-hint">{pastExpanded ? 'collapse' : 'expand'}</span>
            <svg
              className={'rm-chevron' + (pastExpanded ? ' rm-chevron--open' : '')}
              width="13" height="13" viewBox="0 0 14 14" fill="none"
            >
              <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {pastExpanded && (
            <div className="rm-past-items">
              {filteredPast.map(function(item) {
                var isOpen = !!expandedItems[item.id];
                return (
                  <div key={item.id} className="rm-past-item">
                    <button
                      className="rm-past-item-row"
                      onClick={function() { toggleItem(item.id); }}
                      aria-expanded={isOpen}
                    >
                      <span className="rm-past-item-dot" />
                      <span className="rm-past-item-period">{item.period}</span>
                      <span className="rm-past-item-sep" aria-hidden="true">·</span>
                      <span className="rm-past-item-title">{item.title}</span>
                      <span className="rm-past-item-count">{item.features.length}</span>
                      <svg
                        className={'rm-chevron rm-chevron-sm' + (isOpen ? ' rm-chevron--open' : '')}
                        width="11" height="11" viewBox="0 0 14 14" fill="none"
                      >
                        <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    {isOpen && (
                      <ul className="rm-past-feature-list">
                        {item.features.map(function(f, i) {
                          return (
                            <li key={i} className="rm-past-feature-item">
                              <span className="rm-past-feature-bullet" />
                              {f}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Divider ── */}
        <div className="rm-divider">
          <span className="rm-divider-label">DECEMBER 2026 — UPCOMING RELEASES</span>
        </div>

        {/* ── 3-column cards ── */}
        {filteredUpcoming.length === 0 && (
          <p className="rm-empty-filter">No releases in this category yet.</p>
        )}
        {filteredUpcoming.length > 0 && (
        <div className="rm-cards-grid">
          {filteredUpcoming.map(function(item) {
            var isOpen = expandedItems[item.id] !== false;
            var IconComp = ICON_MAP[item.icon] || IconCode;
            return (
              <article key={item.id} className="rm-release-card">
                <div className="rm-release-card-top">
                  <div className="rm-release-icon"><IconComp /></div>
                  <span className="rm-release-badge">Planned</span>
                </div>
                <h2 className="rm-release-title">{item.title}</h2>
                <p className="rm-release-desc">{item.desc}</p>
                <button
                  className={'rm-features-toggle' + (isOpen ? ' rm-features-toggle--open' : '')}
                  onClick={function() { toggleItem(item.id); }}
                  aria-expanded={isOpen}
                >
                  <span>{item.features.length} features</span>
                  <svg
                    className={'rm-chevron rm-chevron-sm' + (isOpen ? ' rm-chevron--open' : '')}
                    width="11" height="11" viewBox="0 0 14 14" fill="none"
                  >
                    <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {isOpen && (
                  <ul className="rm-release-features">
                    {item.features.map(function(f, i) {
                      return (
                        <li key={i} className="rm-release-feature-item">
                          <span className="rm-release-feature-bullet" />
                          {f}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="rm-release-date">{fmtDate(item.releaseDate)}</div>
              </article>
            );
          })}
        </div>
        )}

        <p className="rm-footnote">
          Dates are targets, not guarantees. The roadmap reflects our current development priorities and may shift as the project evolves.
        </p>

      </div>
    </div>
  );
};

export default Roadmap;
